import React, { useState } from 'react';
import { Bell, Users, Shield, Settings } from 'lucide-react';
import { FallDetectionSettings } from '../types';

interface SettingsTabProps {
  settings: FallDetectionSettings;
  onSaveSettings: (settings: FallDetectionSettings) => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ settings, onSaveSettings }) => {
  const [localSettings, setLocalSettings] = useState<FallDetectionSettings>(settings);
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (field: keyof FallDetectionSettings, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactChange = (index: number, field: keyof typeof settings.emergencyContacts[0], value: string) => {
    const newContacts = [...localSettings.emergencyContacts];
    newContacts[index] = {
      ...newContacts[index],
      [field]: value
    };
    setLocalSettings(prev => ({
      ...prev,
      emergencyContacts: newContacts
    }));
  };

  const handleSave = () => {
    onSaveSettings(localSettings);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Alert Settings & Preferences</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Settings className="inline-block mr-2" size={20} />
            Edit Settings
          </button>
        ) : (
          <div className="space-x-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Notification Settings */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <Bell className="text-blue-500 mr-2" size={24} />
            <h3 className="text-lg font-semibold">Notification Settings</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alert Threshold (seconds)
              </label>
              <input
                type="number"
                value={localSettings.alertThreshold}
                onChange={(e) => handleChange('alertThreshold', parseInt(e.target.value))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notification Urgency
              </label>
              <select
                value={localSettings.notificationUrgency}
                onChange={(e) => handleChange('notificationUrgency', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableSoundAlerts}
                  onChange={(e) => handleChange('enableSoundAlerts', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Sound Alerts</span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableMobileNotifications}
                  onChange={(e) => handleChange('enableMobileNotifications', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Mobile Notifications</span>
              </label>
            </div>
          </div>
        </div>

        {/* Emergency Contacts */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <Users className="text-blue-500 mr-2" size={24} />
            <h3 className="text-lg font-semibold">Emergency Contacts</h3>
          </div>
          <div className="space-y-4">
            {localSettings.emergencyContacts.map((contact, index) => (
              <div key={index} className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={contact.name}
                    onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={contact.phone}
                    onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monitoring Settings */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <Shield className="text-blue-500 mr-2" size={24} />
            <h3 className="text-lg font-semibold">Monitoring Settings</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monitoring Sensitivity
              </label>
              <select
                value={localSettings.monitoringSensitivity}
                onChange={(e) => handleChange('monitoringSensitivity', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monitoring Schedule
              </label>
              <select
                value={localSettings.monitoringSchedule}
                onChange={(e) => handleChange('monitoringSchedule', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="24_7">24/7</option>
                <option value="DAYTIME">Daytime Only</option>
                <option value="NIGHTTIME">Nighttime Only</option>
                <option value="CUSTOM">Custom Schedule</option>
              </select>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableAutomatedResponse}
                  onChange={(e) => handleChange('enableAutomatedResponse', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Automated Response</span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableDataLogging}
                  onChange={(e) => handleChange('enableDataLogging', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Data Logging</span>
              </label>
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <Shield className="text-blue-500 mr-2" size={24} />
            <h3 className="text-lg font-semibold">Privacy Settings</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableVideoRecording}
                  onChange={(e) => handleChange('enableVideoRecording', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Video Recording</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Retention Period (days)
              </label>
              <input
                type="number"
                value={localSettings.dataRetentionDays}
                onChange={(e) => handleChange('dataRetentionDays', parseInt(e.target.value))}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localSettings.enableDataSharing}
                  onChange={(e) => handleChange('enableDataSharing', e.target.checked)}
                  disabled={!isEditing}
                  className="rounded text-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <span className="text-sm text-gray-700">Enable Data Sharing with Healthcare Providers</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab; 